import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { StarIcon, QuoteIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
type Review = {
  name: string;
  rating: 4 | 5;
  comment: string;
  service: 'construction' | 'renovation';
  location: string;
};
const reviews: Review[] = [{
  name: 'Marc Puig',
  rating: 5,
  comment: 'Construyeron nuestra casa unifamiliar en Sitges. El resultado superó todas nuestras expectativas.',
  service: 'construction',
  location: 'Sitges'
}, {
  name: 'Laura Martínez',
  rating: 5,
  comment: 'Reforma integral de nuestro piso en el Eixample. Cumplieron plazos y presupuesto.',
  service: 'renovation',
  location: 'Barcelona'
}, {
  name: 'Jordi Ferrer',
  rating: 5,
  comment: 'Muy satisfechos con la reforma integral. El equipo fue muy profesional.',
  service: 'renovation',
  location: 'Terrassa'
}, {
  name: 'Anna Soler',
  rating: 5,
  comment: 'Casa de diseño minimalista en Sant Cugat. Entendieron perfectamente nuestra visión.',
  service: 'construction',
  location: 'Sant Cugat'
}, {
  name: 'Pere Vidal',
  rating: 5,
  comment: 'Reforma completa de nuestro piso antiguo. Transformaron el espacio completamente.',
  service: 'renovation',
  location: 'Sabadell'
}, {
  name: 'Marta Roca',
  rating: 5,
  comment: 'Segunda residencia en el Maresme. Muy buen trabajo con atención al detalle.',
  service: 'construction',
  location: 'Mataró'
}];
function StarRating({
  rating
}: {
  rating: number;
}) {
  return <div className="flex gap-0.5" data-id="element-278">
      {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-3 h-3 ${i < rating ? 'fill-black text-black' : 'fill-neutral-200 text-neutral-200'}`} data-id="element-279" />)}
    </div>;
}
export function ReviewsSection() {
  const {
    t
  } = useLanguage();
  return <section className="w-full bg-neutral-50 py-14 sm:py-20 lg:py-24 px-4 sm:px-6" data-id="element-280">
      <div className="max-w-7xl mx-auto" data-id="element-281">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-8 sm:mb-12" data-id="element-282">
          <span className="text-[10px] sm:text-xs font-medium tracking-widest text-neutral-500 uppercase mb-2 sm:mb-3 block" data-id="element-283">
            {t('reviews.label')}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-black mb-3 sm:mb-4" data-id="element-284">
            {t('reviews.title1')}
            <br data-id="element-285" />
            <span className="text-neutral-400 font-normal" data-id="element-286">
              {t('reviews.title2')}
            </span>
          </h2>
          <p className="text-sm sm:text-base text-neutral-600 max-w-xl mx-auto leading-relaxed font-light" data-id="element-287">
            {t('reviews.subtitle')}
          </p>
        </motion.div>

        {/* Reviews grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-288">
          {reviews.map((review, index) => <motion.div key={index} variants={itemVariants} className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-neutral-100 flex flex-col" data-id="element-289">
              <QuoteIcon className="w-5 h-5 sm:w-6 sm:h-6 text-neutral-200 mb-2 sm:mb-3" data-id="element-290" />

              <p className="text-neutral-700 leading-relaxed mb-3 sm:mb-4 flex-grow text-xs sm:text-sm font-light italic" data-id="element-291">
                "{review.comment}"
              </p>

              <div className="flex items-center justify-between pt-3 border-t border-neutral-100" data-id="element-292">
                <div data-id="element-293">
                  <p className="font-medium text-black text-xs sm:text-sm" data-id="element-294">
                    {review.name}
                  </p>
                  <p className="text-[10px] sm:text-xs text-neutral-500" data-id="element-295">
                    {review.location}
                  </p>
                </div>
                <div className="text-right" data-id="element-296">
                  <StarRating rating={review.rating} data-id="element-297" />
                  <span className={`inline-block mt-1 px-2 py-0.5 text-[9px] sm:text-[10px] font-medium rounded-full ${review.service === 'construction' ? 'bg-black text-white' : 'bg-neutral-100 text-neutral-700'}`} data-id="element-298">
                    {review.service === 'construction' ? t('reviews.tag.construction') : t('reviews.tag.renovation')}
                  </span>
                </div>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Stats - Updated to 1100+ */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="mt-10 sm:mt-16 grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4" data-id="element-299">
          {[{
          value: '1100+',
          label: t('reviews.stat1')
        }, {
          value: '12',
          label: t('reviews.stat2')
        }, {
          value: '98%',
          label: t('reviews.stat3')
        }, {
          value: '50+',
          label: t('reviews.stat4')
        }].map((stat, index) => <motion.div key={index} className="text-center p-3 sm:p-5 bg-white rounded-xl shadow-sm border border-neutral-100" initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1 * index
        }} data-id="element-300">
              <motion.p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light text-black mb-0.5 sm:mb-1" initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.15 + 0.1 * index
          }} data-id="element-301">
                {stat.value}
              </motion.p>
              <p className="text-neutral-600 font-normal text-[10px] sm:text-xs" data-id="element-302">
                {stat.label}
              </p>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}